import React, { useState, useEffect } from 'react';
import { DisplaySettings, Gallery } from '../../types/Gallery';
import '../ui-master-styles.css';
import './DisplaySettings.css';

import { DEFAULT_DISPLAY_SETTINGS } from '../../config/defaults';
import {
  getCurrentDisplaySettings,
  loadGlobalDisplaySettings,
  mergeDisplaySettings,
  readLocalDisplaySettings,
  saveGlobalDisplaySettings,
} from '../../utils/DisplaySettingsAPI';

const DisplaySettingsComponent: React.FC = () => {
  const [settings, setSettings] = useState<DisplaySettings>(DEFAULT_DISPLAY_SETTINGS);
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    loadDisplaySettings();
    loadGalleries();
  }, []);

  const loadDisplaySettings = async () => {
    try {
      const localSettings = readLocalDisplaySettings();
      if (localSettings) {
        setSettings(localSettings);
      }

      const remoteSettings = await loadGlobalDisplaySettings();
      if (remoteSettings) {
        setSettings(remoteSettings);
        return;
      }

      // This browser already has an admin choice that was never published.
      // Store it once so every visitor sees the same homepage.
      if (localSettings && localStorage.getItem('display_settings')) {
        const published = await saveGlobalDisplaySettings(localSettings);
        setSaveMessage(published
          ? 'These settings are now live for everyone on the website.'
          : 'Saved in this browser only. The server could not be reached.');
      }
    } catch (error) {
      console.error('Error loading display settings:', error);
      setSettings(DEFAULT_DISPLAY_SETTINGS);
    }
  };

  const loadGalleries = () => {
    try {
      const adminGalleries = localStorage.getItem('admin_galleries');
      if (adminGalleries) {
        const parsedGalleries = JSON.parse(adminGalleries);
        setGalleries(parsedGalleries.filter((g: any) => g.published && g.images && g.images.length > 0));
      }
    } catch (error) {
      console.error('Error loading galleries:', error);
    }
  };

  const triggerSettingsUpdate = async (newSettings: DisplaySettings) => {
    const settingsToSave = {
      ...newSettings,
      featuredPackageCount: newSettings.featuredPackageCount ?? 3,
      homepageBlogCount: (() => {
        const count = Number(newSettings.homepageBlogCount);
        if (!Number.isFinite(count)) return 3;
        return Math.min(6, Math.max(1, Math.round(count)));
      })()
    };

    setSettings(settingsToSave);
    setSaveMessage('Publishing to all visitors...');
    const published = await saveGlobalDisplaySettings(settingsToSave);
    if (!published) {
      const liveSettings = getCurrentDisplaySettings();
      setSettings(liveSettings);
      const reverted = JSON.stringify(liveSettings) !== JSON.stringify(mergeDisplaySettings(settingsToSave));
      setSaveMessage(reverted
        ? 'Could not reach the server, so the website is still using the previous layout.'
        : 'Could not reach the server. This change is only saved in this browser.');
      return;
    }
    setSaveMessage('Saved. Everyone on the website will see this layout.');
  };


  return (
    <section className="admin-section">
      <div className="display-settings-container">
        <div className="display-settings-header">
          <h2>Display configurations</h2>
          <p>Control how your homepage appears and galleries are displayed. These choices apply to every visitor.</p>
          {saveMessage && (
            <p className="display-settings-status" role="status">{saveMessage}</p>
          )}
        </div>
        
        <div className="settings-grid">
            {/* Homepage Gallery Style - Main Style Selector */}
            <div className="setting-group">
              <label htmlFor="homepage-gallery-style">Homepage Gallery Style</label>
              <div className="chip-selector">
                {[
                  { value: 'masonry-original', label: 'Masonry (Original)', icon: '🧱', description: 'Original masonry layout - keep existing implementation' },
                  { value: 'masonry-new', label: 'Masonry (New)', icon: '🏗️', description: 'New clean masonry built from scratch' },
                  { value: 'carousel', label: 'Carousel', icon: '🎠', description: 'Horizontal sliding galleries' },
                  { value: 'magazine', label: 'Magazine', icon: '📰', description: 'Editorial-style layout' },
                  { value: 'portfolio', label: 'Portfolio', icon: '💼', description: 'Clean professional grid' },
                  { value: 'cinematic', label: 'Cinematic', icon: '🎬', description: 'Full-screen immersive experience' },
                  { value: 'single', label: 'Single', icon: '⭐', description: 'One featured gallery' },
                  { value: 'rotating', label: 'Rotating', icon: '🔄', description: 'Auto-cycling galleries' }
                ].map((option) => (
                  <button
                    key={option.value}
                    className={`chip-option ${(settings.homepageGalleryStyle || 'masonry-original') === option.value ? 'selected' : ''}`}
                    onClick={() => {
                      const newSettings = {...settings, homepageGalleryStyle: option.value as any};
                      triggerSettingsUpdate(newSettings);
                    }}
                    title={option.description}
                  >
                    <span className="chip-icon">{option.icon}</span>
                    <span className="chip-label">{option.label}</span>
                  </button>
                ))}
              </div>
              <small>Choose the main gallery display style for your homepage.</small>
              
              {/* Masonry Size Options - Only show when masonry-original is selected */}
              {(settings.homepageGalleryStyle || 'masonry-original') === 'masonry-original' && (
                <>
                  <label htmlFor="gallery-display-style" style={{ marginTop: '2rem' }}>Masonry Size Variant</label>
                  <div className="chip-selector">
                    {[
                      { value: 'small-mixed', label: 'Small Mixed Grid', icon: '📐', description: 'Ultra compact - maximum galleries per row' },
                      { value: 'medium-mixed', label: 'Medium Mixed Grid', icon: '🎨', description: 'Balanced - standard view with varied sizes' },
                      { value: 'large-mixed', label: 'Large Mixed Grid', icon: '🖼️', description: 'Spacious - gallery showcase with prominent display' },
                      { value: 'masonry', label: 'Masonry Style', icon: '🧱', description: 'Pinterest-style masonry layout with varied heights' },
                      { value: 'masonry-large', label: 'Masonry Large', icon: '🏛️', description: 'Large masonry layout with bigger images' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        className={`chip-option ${(settings.galleryDisplayStyle || 'medium-mixed') === option.value ? 'selected' : ''}`}
                        onClick={() => {
                          const newSettings = {...settings, galleryDisplayStyle: option.value as any};
                          triggerSettingsUpdate(newSettings);
                        }}
                        title={option.description}
                      >
                        <span className="chip-icon">{option.icon}</span>
                        <span className="chip-label">{option.label}</span>
                      </button>
                    ))}
                  </div>
                  <small>Choose the size variant for the original masonry layout. Note: Gallery count above applies regardless of this setting.</small>
                </>
              )}
            </div>

            
            {/* Package Card Display Settings */}
            <div className="setting-group">
              <h3 style={{ marginTop: '2rem', marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 600 }}>Package Card Display Settings</h3>
              <p style={{ marginBottom: '1.5rem', color: '#64748b', fontSize: '0.95rem' }}>Customize how package cards appear on your website</p>
              
              <label htmlFor="package-card-style">Card Style</label>
              <div className="chip-selector">
                {[
                  { value: 'minimal', label: 'Minimal', icon: '✨', description: 'Clean & simple' },
                  { value: 'premium', label: 'Premium', icon: '💎', description: 'Elegant & refined' },
                  { value: 'bold', label: 'Bold', icon: '🔥', description: 'Strong & impactful' },
                  { value: 'elegant', label: 'Elegant', icon: '🎨', description: 'Sophisticated' }
                ].map((option) => (
                  <button
                    key={option.value}
                    className={`chip-option ${settings.packageCardStyle === option.value ? 'selected' : ''}`}
                    onClick={() => {
                      const newSettings = {...settings, packageCardStyle: option.value as any};
                      triggerSettingsUpdate(newSettings);
                    }}
                    title={option.description}
                  >
                    <span className="chip-icon">{option.icon}</span>
                    <span className="chip-label">{option.label}</span>
                  </button>
                ))}
              </div>
              <small>Overall visual style of package cards</small>
              
              <label htmlFor="package-card-columns">Cards Per Row</label>
              <select 
                id="package-card-columns" 
                name="packageCardColumns"
                value={settings.packageCardColumns || 3}
                onChange={(e) => {
                  const newSettings = {...settings, packageCardColumns: parseInt(e.target.value) as 1 | 2 | 3};
                  triggerSettingsUpdate(newSettings);
                }}
              >
                <option value="1">1 Column - Single column layout</option>
                <option value="2">2 Columns - Two cards per row</option>
                <option value="3">3 Columns - Three cards per row (Recommended)</option>
              </select>
              <small>How many package cards to show per row</small>
              
              <label htmlFor="package-card-image-height">Image Height (px)</label>
              <input
                type="number"
                id="package-card-image-height"
                name="packageCardImageHeight"
                min="200"
                max="500"
                step="20"
                value={settings.packageCardImageHeight || 300}
                onChange={(e) => {
                  const newSettings = {...settings, packageCardImageHeight: parseInt(e.target.value)};
                  triggerSettingsUpdate(newSettings);
                }}
              />
              <small>Height of the package image (200-500px). Default: 300px</small>
              
              <label htmlFor="package-card-spacing">Card Spacing (rem)</label>
              <input
                type="number"
                id="package-card-spacing"
                name="packageCardSpacing"
                min="1"
                max="5"
                step="0.5"
                value={settings.packageCardSpacing || 2}
                onChange={(e) => {
                  const newSettings = {...settings, packageCardSpacing: parseFloat(e.target.value)};
                  triggerSettingsUpdate(newSettings);
                }}
              />
              <small>Gap between package cards (1-5rem). Default: 2rem</small>
              
              <label htmlFor="package-card-border-radius">Border Radius (px)</label>
              <input
                type="number"
                id="package-card-border-radius"
                name="packageCardBorderRadius"
                min="0"
                max="32"
                step="2"
                value={settings.packageCardBorderRadius || 16}
                onChange={(e) => {
                  const newSettings = {...settings, packageCardBorderRadius: parseInt(e.target.value)};
                  triggerSettingsUpdate(newSettings);
                }}
              />
              <small>Roundness of card corners (0-32px). Default: 16px</small>
              
              <label htmlFor="package-card-shadow">Shadow Style</label>
              <select 
                id="package-card-shadow" 
                name="packageCardShadow"
                value={settings.packageCardShadow || 'medium'}
                onChange={(e) => {
                  const newSettings = {...settings, packageCardShadow: e.target.value as any};
                  triggerSettingsUpdate(newSettings);
                }}
              >
                <option value="none">None - No shadow</option>
                <option value="subtle">Subtle - Light shadow</option>
                <option value="medium">Medium - Balanced shadow (Recommended)</option>
                <option value="bold">Bold - Strong shadow</option>
              </select>
              <small>Shadow depth for package cards</small>
              
              <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Display Options</h4>
                
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <input
                    type="checkbox"
                    checked={settings.packageCardShowTitle !== false}
                    onChange={(e) => {
                      const newSettings = {...settings, packageCardShowTitle: e.target.checked};
                      triggerSettingsUpdate(newSettings);
                    }}
                  />
                  <span>Show Package Title</span>
                </label>
                
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <input
                    type="checkbox"
                    checked={settings.packageCardShowDescription !== false}
                    onChange={(e) => {
                      const newSettings = {...settings, packageCardShowDescription: e.target.checked};
                      triggerSettingsUpdate(newSettings);
                    }}
                  />
                  <span>Show Description</span>
                </label>
                
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={settings.packageCardShowDuration !== false}
                    onChange={(e) => {
                      const newSettings = {...settings, packageCardShowDuration: e.target.checked};
                      triggerSettingsUpdate(newSettings);
                    }}
                  />
                  <span>Show Duration</span>
                </label>
              </div>
              
              <label htmlFor="featured-package-count" style={{ marginTop: '1.5rem' }}>Featured Packages on Homepage</label>
              <input
                type="number"
                id="featured-package-count"
                name="featuredPackageCount"
                min="1"
                max="12"
                value={settings.featuredPackageCount ?? 3}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  const newValue = isNaN(value) || value < 1 ? 3 : (value > 12 ? 12 : value);
                  const newSettings = {...settings, featuredPackageCount: newValue};
                  triggerSettingsUpdate(newSettings);
                }}
              />
              <small>Number of featured packages to display on the homepage (1-12). Default: 3. Current value: {settings.featuredPackageCount ?? 3}</small>
            </div>
          </div>

          {/* Homepage Section Visibility Controls */}
          <div className="setting-group">
            <h3 style={{ marginTop: '2rem', marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 600, borderBottom: '2px solid #e2e8f0', paddingBottom: '0.5rem' }}>
              Homepage Section Visibility
            </h3>
            <p style={{ marginBottom: '1.5rem', color: '#64748b', fontSize: '0.9rem' }}>
              Toggle sections on/off to customize what appears on your homepage. Hidden sections will not be displayed to visitors.
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div className="checkbox-label" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '0.75rem', background: 'white', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <i className="fas fa-images" style={{ color: 'var(--color-primary, #8B4513)', fontSize: '1.2rem' }}></i>
                    <div>
                      <span style={{ fontWeight: 600, display: 'block' }}>Galleries Section</span>
                      <small style={{ color: '#64748b', fontSize: '0.85rem' }}>Featured galleries showcase</small>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.showGalleriesSection !== false}
                    onChange={(e) => {
                      const newSettings = {...settings, showGalleriesSection: e.target.checked};
                      triggerSettingsUpdate(newSettings);
                    }}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                </label>
                <label htmlFor="homepage-gallery-mode" style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', fontSize: '0.9rem' }}>
                  How many on the homepage
                  <select
                    id="homepage-gallery-mode"
                    value={settings.homepageGalleryMode === 'all' ? 'all' : 'count'}
                    onChange={(event) => {
                      triggerSettingsUpdate({
                        ...settings,
                        homepageGalleryMode: event.target.value === 'all' ? 'all' : 'count',
                      });
                    }}
                    style={{ width: '12rem' }}
                  >
                    <option value="count">A set number</option>
                    <option value="all">All galleries</option>
                  </select>
                </label>
                {settings.homepageGalleryMode === 'all' ? (
                  <small style={{ color: '#64748b' }}>The homepage shows the first 10, then Show more galleries opens the rest.</small>
                ) : (
                  <label htmlFor="gallery-count" style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', fontSize: '0.9rem' }}>
                    <input
                      type="number"
                      id="gallery-count"
                      name="galleryCount"
                      min="1"
                      max="20"
                      value={settings.galleryCount || 11}
                      onChange={(event) => {
                        const value = parseInt(event.target.value, 10);
                        const galleryCount = Number.isNaN(value) ? 11 : Math.min(20, Math.max(1, value));
                        triggerSettingsUpdate({ ...settings, galleryCount, homepageGalleryMode: 'count' });
                      }}
                      style={{ width: '5rem' }}
                    />
                    <small style={{ color: '#64748b' }}>That many galleries appear first. Show more galleries opens the rest. From 1 to 20.</small>
                  </label>
                )}
              </div>

              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '0.75rem', background: 'white', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <i className="fas fa-user" style={{ color: 'var(--color-primary, #8B4513)', fontSize: '1.2rem' }}></i>
                  <div>
                    <span style={{ fontWeight: 600, display: 'block' }}>About Section</span>
                    <small style={{ color: '#64748b', fontSize: '0.85rem' }}>About me and description</small>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.showAboutSection !== false}
                  onChange={(e) => {
                    const newSettings = {...settings, showAboutSection: e.target.checked};
                    triggerSettingsUpdate(newSettings);
                  }}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
              </label>

              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '0.75rem', background: 'white', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <i className="fas fa-box" style={{ color: 'var(--color-primary, #8B4513)', fontSize: '1.2rem' }}></i>
                  <div>
                    <span style={{ fontWeight: 600, display: 'block' }}>Services/Packages Section</span>
                    <small style={{ color: '#64748b', fontSize: '0.85rem' }}>Featured packages display</small>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.showServicesSection !== false}
                  onChange={(e) => {
                    const newSettings = {...settings, showServicesSection: e.target.checked};
                    triggerSettingsUpdate(newSettings);
                  }}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
              </label>

              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '0.75rem', background: 'white', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <i className="fab fa-instagram" style={{ color: 'var(--color-primary, #8B4513)', fontSize: '1.2rem' }}></i>
                  <div>
                    <span style={{ fontWeight: 600, display: 'block' }}>Instagram Section</span>
                    <small style={{ color: '#64748b', fontSize: '0.85rem' }}>Instagram feed display</small>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.showInstagramSection !== false}
                  onChange={(e) => {
                    const newSettings = {...settings, showInstagramSection: e.target.checked};
                    triggerSettingsUpdate(newSettings);
                  }}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
              </label>

              <div className="checkbox-label" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '0.75rem', background: 'white', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <i className="fas fa-blog" style={{ color: 'var(--color-primary, #8B4513)', fontSize: '1.2rem' }}></i>
                    <div>
                      <span style={{ fontWeight: 600, display: 'block' }}>Blog Section</span>
                      <small style={{ color: '#64748b', fontSize: '0.85rem' }}>Stories from Blog posts, in the order set there</small>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.showBlogSection !== false}
                    onChange={(e) => {
                      const newSettings = {...settings, showBlogSection: e.target.checked};
                      triggerSettingsUpdate(newSettings);
                    }}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                </label>
                <label htmlFor="homepage-blog-count" style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', fontSize: '0.9rem' }}>
                  How many on the homepage
                  <input
                    type="number"
                    id="homepage-blog-count"
                    name="homepageBlogCount"
                    min="1"
                    max="6"
                    value={settings.homepageBlogCount ?? 3}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      const homepageBlogCount = Number.isNaN(value) ? 3 : Math.min(6, Math.max(1, value));
                      triggerSettingsUpdate({ ...settings, homepageBlogCount });
                    }}
                    style={{ width: '5rem' }}
                  />
                  <small style={{ color: '#64748b' }}>1–6 published stories. The first one in Blog posts is the large story.</small>
                </label>
              </div>

              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '0.75rem', background: 'white', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <i className="fas fa-envelope" style={{ color: 'var(--color-primary, #8B4513)', fontSize: '1.2rem' }}></i>
                  <div>
                    <span style={{ fontWeight: 600, display: 'block' }}>Contact Section</span>
                    <small style={{ color: '#64748b', fontSize: '0.85rem' }}>Contact information and links</small>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.showContactSection !== false}
                  onChange={(e) => {
                    const newSettings = {...settings, showContactSection: e.target.checked};
                    triggerSettingsUpdate(newSettings);
                  }}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
              </label>
            </div>
          </div>
        
        <div className="form-actions">
          <button 
            type="button" 
            className="btn btn-primary"
            onClick={() => { void triggerSettingsUpdate(settings); }}
          >
            <i className="fas fa-save"></i> Save Display Settings
          </button>
          <button 
            type="button" 
            className="btn btn-outline"
            onClick={() => {
              triggerSettingsUpdate(DEFAULT_DISPLAY_SETTINGS);
            }}
          >
            <i className="fas fa-undo"></i> Reset to Defaults
          </button>
        </div>
      </div>
    </section>
  );
};

export default DisplaySettingsComponent;
